/** `m_abyWaterMap`'s "no body here" (`AreaTerrain.cpp:1016`). */
export const NO_WATER = 0xff;

/** Cells per patch edge, `WATERMAP_XSIZE` (`PRTerrainLib/Terrain.h:35`). */
const PATCH_CELLS = 128;
/** `CELLSCALE`, 200 units, in millimetres (`Terrain.h:54`). */
const CELL_MM = 2_000;
const MM_PER_UNIT = 10;

/** One cell's water: its body in the patch's height table and its corner
 *  alphas in `_CalculateTerrainPatch`'s vertex order. */
export interface WaterCell {
  readonly body: number;
  readonly alphas: readonly [number, number, number, number];
}

/** A map's baked water, as `tools/build_water.py` writes `water/<key>.bin`. */
export class WaterMap {
  private constructor(
    readonly patchesX: number,
    readonly patchesY: number,
    readonly heightScale: number,
    private readonly heights: readonly (readonly number[])[],
    private readonly records: Uint8Array,
  ) {}

  get cellsX(): number {
    return this.patchesX * PATCH_CELLS;
  }

  get cellsY(): number {
    return this.patchesY * PATCH_CELLS;
  }

  static decode(bytes: ArrayBuffer): WaterMap {
    const view = new DataView(bytes);
    if (bytes.byteLength < 14 || String.fromCharCode(...new Uint8Array(bytes, 0, 4)) !== "MWTR") {
      throw new Error("not a water bake");
    }
    let at = 4;
    const version = view.getUint16(at, true); at += 2;
    if (version !== 1) throw new Error(`water bake version ${version}`);
    const patchesX = view.getUint16(at, true); at += 2;
    const patchesY = view.getUint16(at, true); at += 2;
    const heightScale = view.getFloat32(at, true); at += 4;
    const heights: number[][] = [];
    for (let patch = 0; patch < patchesX * patchesY; patch += 1) {
      const layers = view.getUint8(at); at += 1;
      const row: number[] = [];
      for (let layer = 0; layer < layers; layer += 1) {
        row.push(view.getInt32(at, true)); at += 4;
      }
      heights.push(row);
    }
    const cells = patchesX * PATCH_CELLS * patchesY * PATCH_CELLS;
    const records = new Uint8Array(cells * 5);
    let filled = 0;
    while (filled < cells) {
      if (at + 9 > bytes.byteLength) throw new Error("water bake ends before its cells do");
      const run = view.getUint32(at, true); at += 4;
      const record = new Uint8Array(bytes, at, 5); at += 5;
      if (run === 0 || filled + run > cells) throw new Error("water bake run overflows the map");
      for (let i = 0; i < run; i += 1) records.set(record, (filled + i) * 5);
      filled += run;
    }
    return new WaterMap(patchesX, patchesY, heightScale, heights, records);
  }

  /** The water in a map cell, or null where the cell draws none. */
  cell(cellX: number, cellY: number): WaterCell | null {
    if (cellX < 0 || cellY < 0 || cellX >= this.cellsX || cellY >= this.cellsY) return null;
    const at = (cellY * this.cellsX + cellX) * 5;
    const body = this.records[at];
    if (body === NO_WATER) return null;
    return {
      body,
      alphas: [this.records[at + 1], this.records[at + 2], this.records[at + 3], this.records[at + 4]],
    };
  }

  private rawHeight(cellX: number, cellY: number): number | null {
    const water = this.cell(cellX, cellY);
    if (water === null) return null;
    const patch = Math.floor(cellY / PATCH_CELLS) * this.patchesX + Math.floor(cellX / PATCH_CELLS);
    const raw = this.heights[patch]?.[water.body];
    return raw === undefined || raw === -1 ? null : raw;
  }

  /** Where a cell's quad is drawn: its body's height times the map's height
   *  scale (`AreaTerrain.cpp:1062`), before the surface's bob. */
  surfaceZMm(cellX: number, cellY: number): number | null {
    const raw = this.rawHeight(cellX, cellY);
    return raw === null ? null : raw * this.heightScale * MM_PER_UNIT;
  }

  /** `CMapOutdoor::GetWaterHeight` at a world point: the body's height halved
   *  (`AreaTerrain.cpp:527-539`). The cell is found by the cell size; the
   *  original divides by the water map's width (`MapOutdoor.cpp:697-698`),
   *  which reads the wrong cell. */
  heightAt(xMm: number, yMm: number): number | null {
    const raw = this.rawHeight(Math.floor(xMm / CELL_MM), Math.floor(yMm / CELL_MM));
    return raw === null ? null : Math.trunc(raw / 2) * MM_PER_UNIT;
  }
}

/** The water texture on screen at a time: one of thirty, 70 ms each
 *  (`MapOutdoorWater.cpp:43`). */
export function waterFrame(nowMs: number): number {
  return Math.floor(nowMs / 70) % 30;
}

/** The surface's slow bob, `RenderWater`'s static height animation
 *  (`MapOutdoorWater.cpp:74-99`): every `blend` milliseconds, re-rolled to
 *  1-3 s, the end height swaps between 0 and -random(0, 15) units, and the
 *  height eases linearly from where it was toward it. */
export class WaterBob {
  private lastChangeMs: number;
  private blendMs = 300;
  private beginUnits = 0;
  private endUnits = 0;
  private currentUnits = 0;

  constructor(
    startMs: number,
    private readonly roll: (low: number, high: number) => number = (low, high) =>
      low + Math.floor(Math.random() * (high - low + 1)),
  ) {
    this.lastChangeMs = startMs;
  }

  offsetMm(nowMs: number): number {
    if (nowMs - this.lastChangeMs > this.blendMs) {
      this.blendMs = this.roll(1_000, 3_000);
      this.endUnits = this.endUnits === 0 ? -this.roll(0, 15) : 0;
      this.beginUnits = this.currentUnits;
      this.lastChangeMs = nowMs;
    }
    this.currentUnits =
      this.beginUnits + (this.endUnits - this.beginUnits) * ((nowMs - this.lastChangeMs) / this.blendMs);
    return this.currentUnits * MM_PER_UNIT;
  }
}
